import React from 'react';
import {
  FiShield,
  FiLock,
  FiCreditCard,
  FiCheckSquare,
  FiRefreshCw,
  FiFileText,
  FiDollarSign,
} from 'react-icons/fi';
import { Page, LayoutComposer } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './EscrowSecurityPage.module.css';

const FLOW_STEPS = [
  {
    num: 1,
    icon: <FiCreditCard />,
    title: 'Buyer Payment Authorization',
    text: 'Payments are processed through approved third-party banking and payment institutions (USD, EUR, GBP, ACH, SEPA).',
  },
  {
    num: 2,
    icon: <FiLock />,
    title: 'Milestone Production Oversight',
    text: 'Production proceeds under Sourcing Agent oversight while payment schedules align with agreed contractual milestones.',
  },
  {
    num: 3,
    icon: <FiCheckSquare />,
    title: '3-Gate Verification',
    text: 'QC inspection report, Bill of Lading, and China Export Declaration must be audited and approved.',
  },
  {
    num: 4,
    icon: <FiDollarSign />,
    title: 'Milestone Settlement',
    text: 'Supplier disbursements are authorized through approved payment channels in accordance with completed milestones.',
  },
];

const CURRENCIES = [
  'USD ($)',
  'EUR (€)',
  'GBP (£)',
  'ACH (US Direct)',
  'SEPA (EU Direct)',
  'International Wire (SWIFT)',
  'Corporate Transfers',
];

const COMPLIANCE_GATES = [
  'Gate 1: Production Completion & Factory Declaration',
  'Gate 2: On-Site Quality-Control (QC) Inspection Pass',
  'Gate 3: China Customs Export Declaration & Bill of Lading',
];

const EscrowSecurityPage = () => {
  const layoutAreas = `
    topbar
    main
    footer
  `;

  return (
    <Page
      title="Payment & Transaction Security | ChinaDolphins B2B Sourcing"
      description="Learn about ChinaDolphins milestone verification, multi-currency collections via approved payment providers, compliance gates, and buyer protection protocols."
      className={css.root}
    >
      <LayoutComposer areas={layoutAreas}>
        {props => {
          const { Topbar, Main, Footer } = props;
          return (
            <>
              <Topbar as="header">
                <TopbarContainer />
              </Topbar>

              <Main as="main" id="main-content">
                {/* ── Hero ── */}
                <section className={css.hero}>
                  <div className={css.heroInner}>
                    <div className={css.heroBadge}>
                      <FiShield /> Transaction Security &amp; Milestone Controls
                    </div>
                    <h1 className={css.heroTitle}>
                      Payment &amp; Transaction Security
                    </h1>
                    <p className={css.heroSubtitle}>
                      Secure international trade through structured operational controls. Payments are processed through approved third-party payment providers, while ChinaDolphins coordinates milestone verification between global buyers, sourcing agents, and Chinese supply chains.
                    </p>
                  </div>
                </section>

                <section className={css.section}>
                  {/* ── Workflow ── */}
                  <div className={css.sectionHeader}>
                    <div className={css.sectionTag}>Structured Workflow</div>
                    <h2 className={css.sectionTitle}>The Milestone &amp; Transaction Lifecycle</h2>
                  </div>

                  <div className={css.flowGrid}>
                    {FLOW_STEPS.map(step => (
                      <div key={step.num} className={css.flowCard}>
                        <div className={css.flowStepNum}>{step.num}</div>
                        <div className={css.flowIcon}>{step.icon}</div>
                        <h4 className={css.flowTitle}>{step.title}</h4>
                        <p className={css.flowText}>{step.text}</p>
                      </div>
                    ))}
                  </div>

                  {/* ── 4 Feature Pillars ── */}
                  <div className={css.featureGrid}>
                    {/* Multi Currency */}
                    <div className={css.featureCard}>
                      <div className={css.featureIconWrap}>
                        <FiCreditCard />
                      </div>
                      <h3 className={css.featureTitle}>Multi-Currency Collection</h3>
                      <p className={css.featureText}>
                        Depending on the buyer&apos;s jurisdiction, approved third-party payment partners support global business payment methods linked directly to order identifiers:
                      </p>
                      <div className={css.currencyBadges}>
                        {CURRENCIES.map((curr, idx) => (
                          <span key={idx} className={css.currencyBadge}>{curr}</span>
                        ))}
                      </div>
                    </div>

                    {/* Audited Milestone Verification */}
                    <div className={css.featureCard}>
                      <div className={css.featureIconWrap}>
                        <FiLock />
                      </div>
                      <h3 className={css.featureTitle}>Audited Milestone Verification</h3>
                      <p className={css.featureText}>
                        Disbursements under the applicable transaction process are tied strictly to audited production milestones through approved payment rails.
                      </p>
                      <p className={css.featureText} style={{ margin: 0, fontWeight: 600, color: '#0f172a' }}>
                        ✓ Clear milestone governance and verified oversight for international buyers.
                      </p>
                    </div>

                    {/* Compliance Gate */}
                    <div className={css.featureCard}>
                      <div className={css.featureIconWrap}>
                        <FiCheckSquare />
                      </div>
                      <h3 className={css.featureTitle}>The 3 Compliance Gates</h3>
                      <p className={css.featureText}>
                        Before any milestone disbursement is authorized under the payment workflow, three verifiable audit checkpoints must be cleared:
                      </p>
                      <ul className={css.gateList}>
                        {COMPLIANCE_GATES.map((gate, idx) => (
                          <li key={idx} className={css.gateItem}>
                            <FiCheckSquare className={css.gateIcon} />
                            <span>{gate}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Buyer Protection */}
                    <div className={css.featureCard}>
                      <div className={css.featureIconWrap}>
                        <FiRefreshCw />
                      </div>
                      <h3 className={css.featureTitle}>Failed-Pool &amp; Dispute Protection</h3>
                      <p className={css.featureText}>
                        If an aggregation cycle fails to achieve its required MOQ before the published deadline, pre-authorized or collected buyer funds are refunded through the payment provider according to applicable payment terms without cancellation penalties.
                      </p>
                      <p className={css.featureText} style={{ margin: 0 }}>
                        Quality disputes are subject to the 72-hour Sourcing Agent SLA and independent ChinaDolphins Admin arbitration.
                      </p>
                    </div>
                  </div>

                  {/* ── Guarantee / Commitment Box ── */}
                  <div className={css.guaranteeBox}>
                    <div className={css.guaranteeShield}>
                      <FiShield />
                    </div>
                    <div>
                      <h3 className={css.guaranteeTitle}>The ChinaDolphins Transaction Commitment</h3>
                      <p className={css.guaranteeText}>
                        Your orders are governed by transparent milestone verification, live QC inspection reports, independent dispute resolution, and comprehensive legal recourse under our Hong Kong governing framework. All payments are processed through approved third-party payment institutions.
                      </p>
                    </div>
                  </div>
                </section>
              </Main>

              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

export default EscrowSecurityPage;
